// Printable ASCII '!' (33) through '~' (126) — 94 chars, lexicographically orderable.
const CHARS = "!\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~";
const BASE = BigInt(CHARS.length); // 94n
const BASE_N = CHARS.length; // 94
const MIN_CHAR = CHARS[0]; // '!'
const MAX_CHAR = CHARS[CHARS.length - 1]; // '~'
const MID_IDX = Math.floor(BASE_N / 2);
const MID_CHAR = CHARS[MID_IDX]; // 'O' (index 46)
const ci = (c) => CHARS.indexOf(c);
// Encode string as a base-N integer of `len` digits.
// Missing digits are padded with `pad` (0 for lower-bound, BASE_N-1 for upper-bound).
const encode = (s, len, pad) => {
    let v = 0n;
    for (let i = 0; i < len; i++) {
        v = v * BASE + BigInt(i < s.length ? ci(s[i]) : pad);
    }
    return v;
};
// Decode bigint back to a string, stripping trailing MIN_CHARs.
// Valid fractional indices never end with MIN_CHAR.
const decode = (v, len) => {
    let s = "";
    for (let i = 0; i < len; i++) {
        s = CHARS[Number(v % BASE)] + s;
        v /= BASE;
    }
    while (s.length > 1 && s.endsWith(MIN_CHAR))
        s = s.slice(0, -1);
    return s;
};
// Find the midpoint between two strings.
// lo is padded with 0 (= "lo followed by infinitely many MIN_CHARs").
// hi is padded with BASE_N-1 (= "hi followed by infinitely many MAX_CHARs").
// This ensures the numeric midpoint is always strictly between lo and hi as strings,
// provided that neither lo nor hi ends with MIN_CHAR (invariant for valid indices).
const midpoint = (lo, hi) => {
    const len = Math.max(lo.length, hi.length) + 1;
    const a = encode(lo, len, 0);
    const b = encode(hi, len, BASE_N - 1);
    if (a >= b)
        throw new Error(`ordering: lo must be < hi (got "${lo}" >= "${hi}")`);
    return decode((a + b) / 2n, len);
};
// Comparator for entities ordered by a fractional index (Doc 06). FractionalIndex is a
// branded string, so lexicographic comparison is the sort order.
export const byOrder = (a, b) => a.order < b.order ? -1 : a.order > b.order ? 1 : 0;
// Returns the canonical first index for an empty list.
export const first = () => MID_CHAR;
// Returns a FractionalIndex strictly between a and b.
// Pass null for a to get something before b; null for b to get something after a.
//
// Invariant: valid FractionalIndex values never end with MIN_CHAR ('!').
// Inputs that violate this invariant are rejected with a thrown error.
export const between = (a, b) => {
    if (a !== null && a.endsWith(MIN_CHAR))
        throw new Error(`between: invalid index — ends with MIN_CHAR: "${a}"`);
    if (b !== null && b.endsWith(MIN_CHAR))
        throw new Error(`between: invalid index — ends with MIN_CHAR: "${b}"`);
    if (a === null && b === null)
        return first();
    if (a === null)
        return midpoint("", b);
    if (b === null)
        return (a + MAX_CHAR);
    if (a >= b)
        throw new Error(`between: a must be < b (got "${a}" >= "${b}")`);
    return midpoint(a, b);
};
// Comparator for anything carrying a fractional-index `order` field, for use with
// Array.prototype.sort. Plain lexicographic comparison — that's the FractionalIndex
// ordering invariant.
export const compareByOrder = (a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0);
// Append a few random low-order characters to an index. `between` is deterministic,
// so two clients appending to the same list at the same moment compute the *identical*
// key; jittering the freshly-generated append key makes them distinct while keeping
// the ordering correct (the result is still strictly greater than the index it
// extends, and never ends with MIN_CHAR). It does NOT guarantee uniqueness — consumers
// should still break ties deterministically by id when sorting — it just makes a
// collision astronomically unlikely instead of certain. Uses Math.random (ordering
// jitter, not security).
export const jitter = (index, n = 5) => {
    let suffix = "";
    // Indices 1..BASE_N-1 — never MIN_CHAR (index 0), so the result stays a valid index.
    for (let i = 0; i < n; i++)
        suffix += CHARS[1 + Math.floor(Math.random() * (BASE_N - 1))];
    return (index + suffix);
};
// Returns true when consecutive pairs are too close to insert between.
// Only needed for very degenerate orderings; normal use never triggers rebalance.
export const needsRebalance = (indices) => {
    for (let i = 0; i < indices.length - 1; i++) {
        const a = indices[i];
        const b = indices[i + 1];
        if (a >= b)
            return true;
        try {
            const mid = between(a, b);
            if (mid <= a || mid >= b)
                return true;
        }
        catch {
            return true;
        }
    }
    return false;
};
// Produces a fresh, evenly-spaced list of `count` indices.
// Preserves relative order of an existing sequence when used after sorting.
export const rebalance = (count) => {
    if (count === 0)
        return [];
    if (count === 1)
        return [first()];
    const result = [];
    for (let i = 0; i < count; i++) {
        // Distribute across the character space: each item gets a unique index
        // using a two-character base to give ample room for future insertions.
        const hi = Math.floor((i + 1) * (BASE_N - 1) / count);
        const lo = Math.floor(i * (BASE_N - 1) / count);
        const mid = Math.floor((lo + hi) / 2);
        result.push((CHARS[mid] + CHARS[i % BASE_N]));
    }
    // Sort and strip any that end with MIN_CHAR
    return result
        .map(s => (s.endsWith(MIN_CHAR) ? s.slice(0, -1) + CHARS[1] : s))
        .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
};
//# sourceMappingURL=ordering.js.map